import { useCallback } from 'react';

import * as Sentry from '@sentry/react';
import { handleRedirect } from 'App/Helpers/validations/ErrorHelper';

/**
 * Wraps Sharpstar lead/push requests: on success updates hash/offers/leadStatus;
 * on error handles captcha code 24, redirect, and Sentry.
 *
 * @param {object} params
 * @param {string} params.siteUrl
 * @param {string|undefined} params.refid1
 * @param {string} params.lang
 * @param {string} params.env
 * @param {Function} params.setUrlParams
 * @param {Function} params.setOffers
 * @param {Function} params.setLeadStatus
 * @param {Function} params.setCaptchaV2TriggerCode
 * @returns {(request: () => Promise<{lead: object, hash: string, offers: array}>) => Promise<{lead: object, hash: string, offers: array}>}
 */
export function useGamblingReqHandler({
    siteUrl,
    refid1,
    lang,
    env,
    setUrlParams,
    setOffers,
    setLeadStatus,
    setCaptchaV2TriggerCode,
}) {
    return useCallback(
        async (request) => {
            try {
                const { lead, hash, offers } = await request();

                setUrlParams((prev) => ({ ...prev, hash }));
                setOffers(offers);
                setLeadStatus(lead);

                return { lead, hash, offers };
            } catch (error) {
                if (error.code === '24') {
                    setCaptchaV2TriggerCode(error.code);
                }

                handleRedirect({
                    error,
                    fallbackUrl: `${siteUrl}?refid1=${refid1}`,
                    lang,
                    env,
                });

                Sentry.captureException(error);
                throw error;
            }
        },
        [
            siteUrl,
            refid1,
            lang,
            env,
            setUrlParams,
            setOffers,
            setLeadStatus,
            setCaptchaV2TriggerCode,
        ]
    );
}
